import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tabs from '../../../../../components/layouts/Tabs';
import {
  AppointmentRecord,
  MedicationRecord,
  ProcedureRecord,
} from '../../../../../components/EpisodeOfCareRecord';
import {
  ACTIVITY_STATE__ACTIVE,
  ACTIVITY_STATE__SUSPENDED,
} from '../../../../../common/constants';
import {
  APPOINTMENT_STATUS_OPTION_COMPLETED,
  APPOINTMENT_STATUS_OPTION_CANCELED,
  APPOINTMENT_STATUS_OPTION_NO_SHOW,
  MEDICATION_STATUS_OPTION_ADMINISTERED,
  MEDICATION_STATUS_OPTION_CANCELED,
  PROCEDURE_STATUS_OPTION_COMPLETED,
  PROCEDURE_STATUS_OPTION_CANCELED,
  PROCEDURE_STATUS_OPTION_NO_SHOW,
  PROCEDURE_STATUS_OPTION_ABORTED,
} from '../../../../EpisodesOfCare/helpers';

const PAST_STATUSES = new Set([
  APPOINTMENT_STATUS_OPTION_COMPLETED,
  APPOINTMENT_STATUS_OPTION_CANCELED,
  APPOINTMENT_STATUS_OPTION_NO_SHOW,
  MEDICATION_STATUS_OPTION_ADMINISTERED,
  MEDICATION_STATUS_OPTION_CANCELED,
  PROCEDURE_STATUS_OPTION_COMPLETED,
  PROCEDURE_STATUS_OPTION_CANCELED,
  PROCEDURE_STATUS_OPTION_NO_SHOW,
  PROCEDURE_STATUS_OPTION_ABORTED,
]);

const CURRENT_STATES = new Set([
  ACTIVITY_STATE__ACTIVE,
  ACTIVITY_STATE__SUSPENDED,
]);

function filterItemsByStatus(events) {
  const notPast = events.filter((e) => !PAST_STATUSES.has(e.status));

  return {
    past: events.filter((e) => PAST_STATUSES.has(e.status)),
    current: notPast.filter((e) => CURRENT_STATES.has(e.activityState)),
    upcoming: notPast.filter((e) => !CURRENT_STATES.has(e.activityState)),
  };
}

const ClinicalEventsList = ({ 'data-testid': testId, events }) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState('current');

  const tabsContent = filterItemsByStatus(events);

  const getRecord = (event) => {
    switch (event.domain) {
      case 'appointment':
        return <AppointmentRecord appointment={event} showDateStart />;
      case 'drug':
        return <MedicationRecord medication={event} showDateStart />;
      case 'procedure':
        return <ProcedureRecord procedure={event} showDateStart />;
      default:
        return null;
    }
  };

  const tabs = [
    {
      value: 'past',
      label: t('past'),
      content: !isEmpty(tabsContent.past) ? (
        <div className="stack-4">
          {tabsContent.past.map((event) => getRecord(event))}
        </div>
      ) : (
        <p className="text-sm text-center">{t('empty')}</p>
      ),
    },
    {
      value: 'current',
      label: t('current'),
      content: !isEmpty(tabsContent.current) ? (
        <div className="stack-4">
          {tabsContent.current.map((event) => getRecord(event))}
        </div>
      ) : (
        <p className="text-sm text-center">{t('empty')}</p>
      ),
    },
    {
      value: 'upcoming',
      label: t('upcoming'),
      content: !isEmpty(tabsContent.upcoming) ? (
        <div className="stack-4">
          {tabsContent.upcoming.map((event) => getRecord(event))}
        </div>
      ) : (
        <p className="text-sm text-center">{t('empty')}</p>
      ),
    },
  ];

  const handleOnTabChange = (tab) => setCurrentTab(tab);

  return (
    <Tabs
      data-testid={testId}
      activeKey={currentTab}
      onChange={handleOnTabChange}
      tabs={tabs}
      space={0}
      listClassName="w-full justify-between"
    />
  );
};

ClinicalEventsList.propTypes = {
  'data-testid': PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  events: PropTypes.array,
};

ClinicalEventsList.defaultProps = {
  'data-testid': null,
  events: [],
};

export default ClinicalEventsList;
